import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { Mail, Phone, MapPin, Linkedin, Twitter, Instagram, Youtube, MessageCircle, Send } from "lucide-react";
import HawkamLogo from "@/components/HawkamLogo";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export default function Footer() {
  const { t, lang } = useI18n();
  const s = useSiteSettings();
  return (
    <footer className="bg-navy text-cream mt-24" data-testid="site-footer">
      <div className="container-x py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <HawkamLogo size={56} variant="mark" />
            <span className={`text-2xl ${lang === "ar" ? "font-arabic-serif" : "font-serif"}`}>
              {t("brand")}
            </span>
          </div>
          <p className="text-sm text-cream/70 leading-relaxed">
            {lang === "ar"
              ? "منصة متخصصة في تصميم أُطر الحوكمة والامتثال وإدارة المخاطر."
              : "A boutique advisory practice engineering governance, compliance, and risk frameworks."}
          </p>
        </div>

        <div>
          <div className="eyebrow mb-4">{lang === "ar" ? "الموقع" : "Site"}</div>
          <ul className="space-y-2 text-sm">
            <li><Link className="hover:text-gold" to="/about">{t("nav.about")}</Link></li>
            <li><Link className="hover:text-gold" to="/services">{t("nav.services")}</Link></li>
            <li><Link className="hover:text-gold" to="/campaigns">{t("nav.campaigns")}</Link></li>
          </ul>
        </div>

        <div>
          <div className="eyebrow mb-4">{lang === "ar" ? "الحوكمة" : "Governance"}</div>
          <ul className="space-y-2 text-sm">
            <li><Link className="hover:text-gold" to="/policies">{lang === "ar" ? "وثائق الحوكمة" : "Governance Docs"}</Link></li>
            <li><Link className="hover:text-gold" to="/policies/privacy">{lang === "ar" ? "سياسة الخصوصية" : "Privacy Policy"}</Link></li>
            <li><Link className="hover:text-gold" to="/data-rights" data-testid="footer-data-rights">{lang === "ar" ? "حقوق أصحاب البيانات" : "Data Subject Rights"}</Link></li>
            <li><Link className="hover:text-gold" to="/policies/terms">{lang === "ar" ? "شروط الاستخدام" : "Terms of Service"}</Link></li>
            <li><Link className="hover:text-gold" to="/policies/cookies">{lang === "ar" ? "سياسة الكوكيز" : "Cookie Policy"}</Link></li>
          </ul>
        </div>

        <div>
          <div className="eyebrow mb-4">{lang === "ar" ? "تواصل" : "Contact"}</div>
          <ul className="space-y-3 text-sm text-cream/80">
            {s.contact_email && (
              <li className="flex items-center gap-3">
                <Mail size={14} className="text-gold" />
                <a href={`mailto:${s.contact_email}`} className="hover:text-gold" data-testid="footer-email">{s.contact_email}</a>
              </li>
            )}
            {s.contact_phone && (
              <li className="flex items-center gap-3">
                <Phone size={14} className="text-gold" />
                <a href={`tel:${s.contact_phone.replace(/\s+/g, "")}`} className="hover:text-gold" data-testid="footer-phone">{s.contact_phone}</a>
              </li>
            )}
            {s.whatsapp && (
              <li className="flex items-center gap-3">
                <MessageCircle size={14} className="text-gold" />
                <a href={`https://wa.me/${s.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="hover:text-gold" data-testid="footer-whatsapp">
                  {lang === "ar" ? "واتساب" : "WhatsApp"} · {s.whatsapp}
                </a>
              </li>
            )}
            {s.telegram && (
              <li className="flex items-center gap-3">
                <Send size={14} className="text-gold" />
                <a href={s.telegram.startsWith("http") ? s.telegram : `https://t.me/${s.telegram.replace(/^@/, "")}`} target="_blank" rel="noopener noreferrer" className="hover:text-gold" data-testid="footer-telegram">
                  {lang === "ar" ? "تلغرام" : "Telegram"}
                </a>
              </li>
            )}
            {(s.address_ar || s.address_en) && (
              <li className="flex items-center gap-3">
                <MapPin size={14} className="text-gold" />
                <span data-testid="footer-address">
                  {(lang === "ar" ? s.address_ar : s.address_en) || s.address_en || s.address_ar}
                </span>
              </li>
            )}
          </ul>
          {(s.linkedin_url || s.twitter_url || s.telegram || s.youtube_url) && (
            <div className="mt-5 flex items-center gap-3 text-cream/70">
              {s.linkedin_url && <a href={s.linkedin_url} target="_blank" rel="noopener noreferrer" className="hover:text-gold" aria-label="LinkedIn"><Linkedin size={16} /></a>}
              {s.twitter_url && <a href={s.twitter_url} target="_blank" rel="noopener noreferrer" className="hover:text-gold" aria-label="X / Twitter"><Twitter size={16} /></a>}
              {s.telegram && (
                <a
                  href={s.telegram.startsWith("http") ? s.telegram : `https://t.me/${s.telegram.replace(/^@/, "")}`}
                  target="_blank" rel="noopener noreferrer"
                  className="hover:text-gold" aria-label="Telegram"
                  data-testid="footer-social-telegram"
                >
                  <Send size={16} />
                </a>
              )}
              {s.youtube_url && <a href={s.youtube_url} target="_blank" rel="noopener noreferrer" className="hover:text-gold" aria-label="YouTube"><Youtube size={16} /></a>}
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-gold/15">
        <div className="container-x py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-cream/60">
          <div>
            {lang === "ar"
              ? `جميع الحقوق محفوظة © ${new Date().getFullYear()} HAWKAM Consulting`
              : `© ${new Date().getFullYear()} HAWKAM Consulting — All rights reserved`}
          </div>
          <div>{lang === "ar" ? "مصمّم وفق مبادئ حوكمة المنصات" : "Built to platform-governance standards"}</div>
        </div>
      </div>
    </footer>
  );
}
